import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { PaneComponent } from '@rocksoft/cdk';
import { AppleWifiProfilePreset, AppleWifiProfilePresetsService } from '@rocksoft/mdm-sdk-v2';
import { isNotNullOrUndefined } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { ConfigurationProfile } from '../../../viewmodels/apple/configuration-profile.model';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';
import { WifiPresetInstallComponent } from './wifi-preset-install.component';
@Component({
  selector: 'ml-mdm-wifi-preset-install',
  templateUrl: './wifi-preset-install.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, WifiPresetInstallComponent, AsyncPipe],
})
export class WifiPresetInstallContainerComponent {
  private readonly appleWifiProfilePresetsService = inject(AppleWifiProfilePresetsService);
  private readonly route = inject(ActivatedRoute);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly mdmService = inject(AppleMdmService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly udid = signal('');
  private readonly udid$: Observable<string> = toObservable(this.udid).pipe(
    filter(isNotNullOrUndefined),
    distinctUntilChanged()
  );

  readonly wifiPresets: Observable<AppleWifiProfilePreset[]> =
    this.appleWifiProfilePresetsService.getWifiProfilePresets({ apiVersion: '2.0' });
  selectedWifiProfile: AppleWifiProfilePreset;
  configurationProfile = new ConfigurationProfile();
  success = false;

  private _commandProgress = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'InstallProfile'))
    )
  );

  constructor() {
    this.configurationProfile.payloadIdentifier = 'com.methylium.wifi.guest';
    this.configurationProfile.payloadOrganization = 'Methylium B.V.';
    this.configurationProfile.payloadDescription = 'This profile has pre-configured settings for Wi-Fi.';
    this.configurationProfile.payloadDisplayName = 'Wi-Fi';

    effect((onCleanup) => {
      const params$ = this.route.parent?.params;
      if (!params$) {
        this.udid.set('');
        return;
      }
      const sub = params$
        .pipe(
          map((p: Params) => p.udid),
          filter(isNotNullOrUndefined),
          distinctUntilChanged()
        )
        .subscribe((id) => {
          this.udid.set(id);
        });

      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  installProfile(profile: ConfigurationProfile) {
    this.success = false;
    const _udid = this.udid;
    if (!_udid) return;
    const command: CommandProgress = {
      udid: _udid(),
      commandUUID: '',
      status: 'Sending',
      requestType: 'InstallProfile',
    };
    this._commandProgress.next(command);
    this.mdmService
      .installProfile(_udid(), profile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.success = true;
        },
      });
  }
}
